// Sticky bottom bar showing currently selected tools for comparison.
// Hidden when the tray is empty or when already on /compare. Mobile-aware:
// sits above the existing MobileBottomNav by using bottom-16 on small
// screens. Pulls names lazily from the tools cache so chips show real names
// instead of slugs.
import { Link, useLocation } from "react-router-dom";
import { GitCompareArrows, X } from "lucide-react";
import { useCompareTray, COMPARE_MAX } from "@/hooks/useCompareTray";
import { useTools } from "@/hooks/useTools";
import { slugify } from "@/pages/ToolDetail";
import ToolLogo from "@/components/ToolLogo";
import { useMemo } from "react";

const CompareTrayBar = () => {
  const { slugs, remove, clear } = useCompareTray();
  const { tools } = useTools();
  const location = useLocation();

  const toolBySlug = useMemo(() => {
    const map = new Map<string, { name: string; logoUrl?: string }>();
    tools.forEach((t) => map.set(slugify(t.name), { name: t.name, logoUrl: t.logoUrl }));
    return map;
  }, [tools]);

  if (slugs.length === 0) return null;
  if (location.pathname.startsWith("/compare")) return null;

  const canCompare = slugs.length >= 2;

  return (
    <div
      className="fixed inset-x-0 bottom-16 md:bottom-4 z-40 px-3 pointer-events-none"
      role="region"
      aria-label="Compare tray"
    >
      <div className="mx-auto max-w-3xl pointer-events-auto">
        <div className="flex flex-wrap items-center gap-2 rounded-xl border border-primary/30 bg-card/95 backdrop-blur-md shadow-lg px-3 py-2">
          <div className="flex items-center gap-1.5 text-xs font-mono text-muted-foreground shrink-0">
            <GitCompareArrows className="w-3.5 h-3.5 text-primary" />
            <span>
              Compare <span className="text-foreground">{slugs.length}</span>/{COMPARE_MAX}
            </span>
          </div>
          <div className="flex flex-wrap items-center gap-1.5 flex-1 min-w-0">
            {slugs.map((s) => {
              const t = toolBySlug.get(s);
              const name = t?.name || s;
              return (
                <span
                  key={s}
                  className="inline-flex items-center gap-1 text-[11px] font-mono pl-1 pr-2 py-0.5 rounded-md border border-border bg-secondary text-foreground"
                >
                  <ToolLogo name={name} logoUrl={t?.logoUrl} className="w-4 h-4" />
                  <span className="truncate max-w-[120px]">{name}</span>
                  <button
                    type="button"
                    onClick={() => remove(s)}
                    aria-label={`Remove ${name} from compare`}
                    className="text-muted-foreground hover:text-foreground"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              );
            })}
          </div>
          <div className="flex items-center gap-1.5 ml-auto shrink-0">
            <button
              type="button"
              onClick={clear}
              className="text-[11px] font-mono px-2 py-1 rounded-md text-muted-foreground hover:text-foreground"
            >
              Clear
            </button>
            <Link
              to={canCompare ? `/compare?ids=${slugs.join(",")}` : "#"}
              aria-disabled={!canCompare}
              onClick={(e) => {
                if (!canCompare) e.preventDefault();
              }}
              className={`text-[11px] font-mono px-3 py-1 rounded-md transition-colors ${
                canCompare
                  ? "bg-primary text-primary-foreground hover:bg-primary/90"
                  : "bg-secondary text-muted-foreground cursor-not-allowed"
              }`}
            >
              Compare ({slugs.length})
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompareTrayBar;
